import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { e as emblaShopifyBlockSelect } from './shopifyBlockSelect-C3cIAIvp.js';

const defaultOptions$1 = {
  active: true,
  breakpoints: {}
};

function clampNumber(number, min, max) {
  return Math.min(Math.max(number, min), max);
}
function isNumber(value) {
  return typeof value === 'number' && !isNaN(value);
}

function Fade(userOptions = {}) {
  const fullOpacity = 1;
  const noOpacity = 0;
  const fadeFriction = 0.68;
  let options;
  let emblaApi;
  let isSsr = false;
  let destroyed = false;
  let opacities = [];
  let fadeToNextDistance;
  let distanceFromPointerDown = 0;
  let fadeVelocity = 0;
  let progress = 0;
  let shouldFadePair = false;
  let defaultSettledBehaviour;
  let defaultProgressBehaviour;
  function pluginIsActive() {
    if (isSsr) return false;
    if (destroyed) return false;
    return options.active;
  }
  function init(emblaApiInstance, optionsHandler) {
    emblaApi = emblaApiInstance;
    const {
      mergeOptions,
      optionsAtMedia
    } = optionsHandler;
    const optionsBase = mergeOptions(defaultOptions$1, Fade.globalOptions);
    const allOptions = mergeOptions(optionsBase, userOptions);
    destroyed = false;
    options = optionsAtMedia(allOptions);
    isSsr = emblaApi.internalEngine().isSsr;
    if (!pluginIsActive()) return;
    const selectedSnap = emblaApi.selectedSnap();
    const {
      scrollBody,
      containerRect,
      axis
    } = emblaApi.internalEngine();
    const containerSize = axis.getSize(containerRect);
    fadeToNextDistance = clampNumber(containerSize * 0.75, 200, 500);
    shouldFadePair = false;
    opacities = emblaApi.snapList().map((_, index) => index === selectedSnap ? fullOpacity : noOpacity);
    defaultSettledBehaviour = scrollBody.settled;
    defaultProgressBehaviour = emblaApi.scrollProgress;
    scrollBody.settled = fadeSettled;
    emblaApi.scrollProgress = fadeScrollProgress;
    emblaApi.on('select', select).on('slidefocus', fadeToSelectedSnapInstantly).on('pointerdown', pointerDown).on('pointerup', pointerUp);
    disableScroll();
    fadeToSelectedSnapInstantly();
  }
  function destroy() {
    if (!pluginIsActive()) return;
    const {
      scrollBody
    } = emblaApi.internalEngine();
    scrollBody.settled = defaultSettledBehaviour;
    emblaApi.scrollProgress = defaultProgressBehaviour;
    emblaApi.off('select', select).off('slidefocus', fadeToSelectedSnapInstantly).off('pointerdown', pointerDown).off('pointerup', pointerUp);
    emblaApi.slideNodes().forEach(slideNode => {
      const slideStyle = slideNode.style;
      slideStyle.opacity = '';
      slideStyle.pointerEvents = '';
    });
    destroyed = true;
  }
  function fadeToSelectedSnapInstantly() {
    const selectedSnap = emblaApi.selectedSnap();
    setOpacities(selectedSnap, fullOpacity);
  }
  function fadeScrollProgress() {
    return progress;
  }
  function pointerUp() {
    shouldFadePair = false;
  }
  function pointerDown() {
    shouldFadePair = false;
    distanceFromPointerDown = 0;
    fadeVelocity = 0;
  }
  function select() {
    const duration = emblaApi.internalEngine().scrollBody.duration();
    fadeVelocity = duration ? 0 : fullOpacity;
    shouldFadePair = true;
    if (!duration) fadeToSelectedSnapInstantly();
  }
  function getSlideTransform(position) {
    const {
      axis
    } = emblaApi.internalEngine();
    const translateAxis = axis.scroll.toUpperCase();
    return `translate${translateAxis}(${axis.direction(position)}px)`;
  }
  function disableScroll() {
    const {
      translate,
      slideTranslates
    } = emblaApi.internalEngine();
    const translates = [translate, ...slideTranslates];
    translates.forEach(translate => {
      translate.clear();
      translate.toggleActive(false);
    });
    emblaApi.containerNode().style.transform = 'translate(0px,0px)';
  }
  function lockExcessiveScroll(fadeIndex) {
    const {
      scrollSnaps,
      location,
      target
    } = emblaApi.internalEngine();
    if (!isNumber(fadeIndex) || opacities[fadeIndex] < 0.5) return;
    location.set(scrollSnaps[fadeIndex]);
    target.set(location);
  }
  function setOpacities(fadeIndex, velocity) {
    const scrollSnaps = emblaApi.snapList();
    scrollSnaps.forEach((_, indexA) => {
      const absVelocity = Math.abs(velocity);
      const currentOpacity = opacities[indexA];
      const isFadeIndex = indexA === fadeIndex;
      const nextOpacity = isFadeIndex ? currentOpacity + absVelocity : currentOpacity - absVelocity;
      const clampedOpacity = clampNumber(nextOpacity, noOpacity, fullOpacity);
      opacities[indexA] = clampedOpacity;
      const fadePair = isFadeIndex && shouldFadePair;
      const indexB = emblaApi.previousSnap();
      if (fadePair) opacities[indexB] = 1 - clampedOpacity;
      if (isFadeIndex) setProgress(fadeIndex, clampedOpacity);
      setOpacity(indexA);
    });
  }
  function setOpacity(index) {
    const {
      scrollSnaps,
      containerRect,
      scrollSnapList
    } = emblaApi.internalEngine();
    const slidesInSnap = scrollSnapList.slidesBySnap[index];
    const opacity = opacities[index];
    slidesInSnap.forEach(slideIndex => {
      const slideStyle = emblaApi.slideNodes()[slideIndex].style;
      const roundedOpacity = parseFloat(opacity.toFixed(2));
      const hasOpacity = roundedOpacity > noOpacity;
      const position = hasOpacity ? scrollSnaps[index] : containerRect.width + 2;
      const transform = getSlideTransform(position);
      if (hasOpacity) slideStyle.transform = transform;
      slideStyle.opacity = roundedOpacity.toString();
      slideStyle.pointerEvents = opacity > 0.5 ? 'auto' : 'none';
      if (!hasOpacity) slideStyle.transform = transform;
    });
  }
  function setProgress(fadeIndex, opacity) {
    const {
      indexCurrent,
      dragHandler,
      scrollSnaps
    } = emblaApi.internalEngine();
    const pointerDown = dragHandler.pointerDown();
    const snapFraction = 1 / (scrollSnaps.length - 1);
    let indexA = fadeIndex;
    let indexB = pointerDown ? emblaApi.selectedSnap() : emblaApi.previousSnap();
    if (pointerDown && indexA === indexB) {
      const reverseSign = Math.sign(distanceFromPointerDown) * -1;
      indexA = indexB;
      indexB = indexCurrent.clone().set(indexB).add(reverseSign).get();
    }
    const currentPosition = indexB * snapFraction;
    const diffPosition = (indexA - indexB) * snapFraction;
    progress = currentPosition + diffPosition * opacity;
  }
  function getFadeIndex() {
    const {
      dragHandler,
      indexCurrent,
      scrollBody
    } = emblaApi.internalEngine();
    const selectedSnap = emblaApi.selectedSnap();
    if (!dragHandler.pointerDown()) return selectedSnap;
    const directionSign = Math.sign(scrollBody.velocity());
    const distanceSign = Math.sign(distanceFromPointerDown);
    const nextSnap = indexCurrent.clone().set(selectedSnap).add(directionSign * -1).get();
    if (!directionSign || !distanceSign) return null;
    return distanceSign === directionSign ? nextSnap : selectedSnap;
  }
  function fade(emblaApi) {
    const {
      dragHandler,
      scrollBody
    } = emblaApi.internalEngine();
    const pointerDown = dragHandler.pointerDown();
    const velocity = scrollBody.velocity();
    const duration = scrollBody.duration();
    const fadeIndex = getFadeIndex();
    const noFadeIndex = !isNumber(fadeIndex);
    if (pointerDown) {
      if (!velocity) return;
      distanceFromPointerDown += velocity;
      fadeVelocity = Math.abs(velocity / fadeToNextDistance);
      lockExcessiveScroll(fadeIndex);
    }
    if (!pointerDown) {
      if (!duration || noFadeIndex) return;
      fadeVelocity += (fullOpacity - opacities[fadeIndex]) / duration;
      fadeVelocity *= fadeFriction;
    }
    if (noFadeIndex) return;
    setOpacities(fadeIndex, fadeVelocity);
  }
  function fadeSettled() {
    const {
      target,
      location
    } = emblaApi.internalEngine();
    const displacement = target.minus(location);
    const notReachedTarget = Math.abs(displacement) >= 1;
    const fadeIndex = getFadeIndex();
    const noFadeIndex = !isNumber(fadeIndex);
    fade(emblaApi);
    if (noFadeIndex || notReachedTarget) return false;
    return opacities[fadeIndex] > 0.999;
  }
  function createFadeSsrStyles(options, containerSelector, slidesSelector) {
    const {
      active
    } = options;
    const {
      slidesBySnap
    } = emblaApi.internalEngine().scrollSnapList;
    const selectedSnap = emblaApi.selectedSnap();
    const opacity = active ? 0 : 1;
    const pointerEvents = active ? 'none' : 'auto';
    const baseStyles = `${containerSelector} ${slidesSelector}{opacity:${opacity};pointer-events:${pointerEvents};}`;
    const slideStyles = slidesBySnap[selectedSnap].reduce((styles, index) => {
      return styles + `${containerSelector} ${slidesSelector}:nth-child(${index + 1}){opacity:1;pointer-events:auto;}`;
    }, '');
    if (active) return baseStyles + slideStyles;
    return baseStyles;
  }
  function ssrStyles(containerSelector, slidesSelector = '> *') {
    if (!isSsr) return '';
    const optionBreakpoints = userOptions.breakpoints || {};
    const baseStyles = createFadeSsrStyles(options, containerSelector, slidesSelector);
    const mediaStyles = Object.keys(optionBreakpoints).reduce((styles, key) => {
      return styles + `@media ${key}{${createFadeSsrStyles(optionBreakpoints[key], containerSelector, slidesSelector)}}`;
    }, '');
    return baseStyles + mediaStyles;
  }
  const self = {
    name: 'fade',
    options: userOptions,
    init,
    destroy,
    ssrStyles
  };
  return self;
}
Fade.globalOptions = undefined;

const defaultOptions = {
  active: true,
  breakpoints: {},
  delay: 4000,
  instant: false,
  defaultInteraction: true,
  stopOnLastSnap: false,
  rootNode: null
};

function normalizeDelay(emblaApi, delay) {
  const scrollSnaps = emblaApi.snapList();
  if (typeof delay === 'number') {
    return scrollSnaps.map(() => delay);
  }
  return delay(scrollSnaps, emblaApi);
}
function getAutoplayRootNode(emblaApi, rootNode) {
  const emblaRootNode = emblaApi.rootNode();
  return rootNode && rootNode(emblaRootNode) || emblaRootNode;
}

function Autoplay(userOptions = {}) {
  let options;
  let emblaApi;
  let isSsr = false;
  let destroyed = false;
  let delay;
  let pauseDelay = null;
  let timerStartTime = null;
  let timerId = 0;
  let autoplayRunning = false;
  let playOnDocumentVisible = false;
  let instant = false;
  let isMouseOver = false;
  let isPointerDown = false;
  let onInteraction = onDefaultInteraction;
  function pluginIsActive() {
    if (isSsr) return false;
    if (destroyed) return false;
    return options.active;
  }
  function init(emblaApiInstance, optionsHandler) {
    emblaApi = emblaApiInstance;
    const {
      mergeOptions,
      optionsAtMedia
    } = optionsHandler;
    const optionsBase = mergeOptions(defaultOptions, Autoplay.globalOptions);
    const allOptions = mergeOptions(optionsBase, userOptions);
    destroyed = false;
    options = optionsAtMedia(allOptions);
    isSsr = emblaApi.internalEngine().isSsr;
    if (!pluginIsActive()) return;
    if (emblaApi.snapList().length <= 1) return;
    instant = options.instant;
    delay = normalizeDelay(emblaApi, options.delay);
    onInteraction = options.defaultInteraction ? onDefaultInteraction : onCustomInteraction;
    const {
      eventStore,
      nodeHandler
    } = emblaApi.internalEngine();
    const {
      ownerDocument
    } = nodeHandler;
    const root = getAutoplayRootNode(emblaApi, options.rootNode);
    if (ownerDocument) {
      eventStore.add(ownerDocument, 'visibilitychange', onVisibilityChange);
    }
    emblaApi.on('pointerdown', onInteraction);
    emblaApi.on('pointerup', onInteraction);
    emblaApi.on('slidefocus', onInteraction);
    eventStore.add(root, 'mouseenter', event => onInteraction(emblaApi, event, event.type));
    eventStore.add(root, 'mouseleave', event => onInteraction(emblaApi, event, event.type));
    eventStore.add(emblaApi.containerNode(), 'focusout', event => onInteraction(emblaApi, event, 'slidefocusout'));
  }
  function destroy() {
    if (!pluginIsActive()) return;
    emblaApi.off('pointerdown', onInteraction).off('pointerup', onInteraction).off('slidefocus', onInteraction);
    stopAutoplay();
    destroyed = true;
    autoplayRunning = false;
  }
  function setTimer() {
    const {
      ownerWindow
    } = emblaApi.internalEngine().nodeHandler;
    if (!ownerWindow) return;
    const startTime = new Date().getTime();
    const event = emblaApi.createEvent('autoplay:timerset', {
      startTime
    });
    ownerWindow.clearTimeout(timerId);
    timerId = ownerWindow.setTimeout(next, getDelay());
    timerStartTime = startTime;
    event.emit();
  }
  function clearTimer() {
    const {
      ownerWindow
    } = emblaApi.internalEngine().nodeHandler;
    if (!ownerWindow) return;
    const stopTime = new Date().getTime();
    const event = emblaApi.createEvent('autoplay:timerstopped', {
      stopTime
    });
    ownerWindow.clearTimeout(timerId);
    timerId = 0;
    timerStartTime = null;
    event.emit();
  }
  function startAutoplay() {
    if (!pluginIsActive()) return;
    if (documentIsHidden()) {
      playOnDocumentVisible = true;
      return;
    }
    if (!autoplayRunning) {
      autoplayRunning = true;
      const event = emblaApi.createEvent('autoplay:play', null);
      event.emit();
    }
    setTimer();
  }
  function stopAutoplay() {
    if (!pluginIsActive()) return;
    if (autoplayRunning) {
      autoplayRunning = false;
      const event = emblaApi.createEvent('autoplay:stop', null);
      event.emit();
    }
    clearTimer();
  }
  function onVisibilityChange() {
    if (documentIsHidden()) {
      playOnDocumentVisible = autoplayRunning;
      return stopAutoplay();
    }
    if (playOnDocumentVisible) startAutoplay();
  }
  function documentIsHidden() {
    const {
      ownerDocument
    } = emblaApi.internalEngine().nodeHandler;
    if (!ownerDocument) return false;
    return ownerDocument.visibilityState === 'hidden';
  }
  function onDefaultInteraction(_, originalEvent, customType) {
    const type = originalEvent.type;
    const interaction = customType || type;
    if (interaction === 'slidefocus') stopAutoplay();
    if (interaction === 'pointerdown') stopAutoplay();
  }
  function onCustomInteraction(_, originalEvent, customType) {
    const type = originalEvent.type;
    const interaction = customType || type;
    if (interaction === 'mouseenter') isMouseOver = true;
    if (interaction === 'mouseleave') isMouseOver = false;
    if (interaction === 'pointerdown') isPointerDown = true;
    if (interaction === 'pointerup') isPointerDown = false;
    const event = emblaApi.createEvent('autoplay:interaction', {
      interaction,
      originalEvent,
      isMouseOver,
      isPointerDown
    });
    event.emit();
  }
  function play(instantOverride) {
    instant = instantOverride !== null && instantOverride !== void 0 ? instantOverride : instant;
    startAutoplay();
  }
  function stop() {
    if (autoplayRunning) stopAutoplay();
  }
  function reset() {
    if (autoplayRunning) startAutoplay();
    pauseDelay = null;
  }
  function pause() {
    if (!autoplayRunning) return;
    pauseDelay = timeUntilNext();
    stopAutoplay();
  }
  function isPlaying() {
    return autoplayRunning;
  }
  function next() {
    const {
      indexCurrent
    } = emblaApi.internalEngine();
    const nextIndex = indexCurrent.clone().add(1).get();
    const lastIndex = emblaApi.snapList().length - 1;
    const kill = options.stopOnLastSnap && nextIndex === lastIndex;
    const event = emblaApi.createEvent('autoplay:select', {
      targetSnap: emblaApi.canGoToNext() ? nextIndex : 0,
      sourceSnap: indexCurrent.get()
    });
    if (emblaApi.canGoToNext()) {
      emblaApi.goToNext(instant);
    } else {
      emblaApi.goTo(0, instant);
    }
    event.emit();
    pauseDelay = null;
    if (kill) return stopAutoplay();
    startAutoplay();
  }
  function getDelay() {
    return pauseDelay || delay[emblaApi.selectedSnap()];
  }
  function timeUntilNext() {
    if (!pluginIsActive()) return null;
    if (!timerStartTime) return null;
    const timePastSinceStart = new Date().getTime() - timerStartTime;
    return getDelay() - timePastSinceStart;
  }
  const self = {
    name: 'autoplay',
    options: userOptions,
    init,
    destroy,
    play,
    stop,
    reset,
    pause,
    isPlaying,
    timeUntilNext
  };
  return self;
}
Autoplay.globalOptions = undefined;

class AnnouncementBar extends HTMLElement {
  #viewport = this.querySelector('.embla__viewport');
  /** @type {import('embla-carousel').EmblaCarouselType} */
  #embla;
  /** @type {import('embla-carousel-autoplay').AutoplayType | undefined} */
  #autoplay;

  connectedCallback() {
    this.#initCarousel();
  }

  disconnectedCallback() {
    this.#embla?.destroy();
  }

  /**
   * Initialize the fading, auto-playing carousel.
   * `defaultInteraction: false` keeps autoplay running through pointer/focus
   * interaction so it never stops once started.
   */
  #initCarousel() {
    if (!this.#viewport) return;

    this.#embla = EmblaCarousel(this.#viewport, { loop: true }, [
      Fade(),
      Autoplay({ delay: 4000, defaultInteraction: false }),
    ]);

    emblaShopifyBlockSelect(this.#embla, this.#viewport);

    if (this.#embla.snapList().length <= 1) return;
    this.#autoplay = this.#embla.plugins().autoplay;
    this.#autoplay?.play();
  }
}

customElements.define('announcement-bar', AnnouncementBar);
